import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { SearchIcon } from '@/shared/icons';
import { focusRingClassNameStrong, formControlSurfaceClassName } from '@/shared/config/theme';
import { textStyles } from '@/shared/tokens/typography';
import { cn } from '@/shared/lib/cn';
import SearchClassResultRow from '@/features/search/SearchClassResultRow';
import SearchModeToggle from '@/features/search/SearchModeToggle';
import SearchUserResultRow from '@/features/search/SearchUserResultRow';
import type { SearchMode } from '@/features/search/types';
import { useClassTypeaheadQuery } from '@/features/search/useClassTypeaheadQuery';
import { useUserTypeaheadQuery } from '@/features/search/useUserTypeaheadQuery';

interface GlobalSearchProps {
  className?: string;
  placeholder?: string;
}

const DEFAULT_SEARCH_MODE: SearchMode = 'users';

export default function GlobalSearch({ className, placeholder }: GlobalSearchProps) {
  const { t } = useTranslation('search');
  const resolvedPlaceholder = placeholder ?? t('placeholder');
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [searchMode, setSearchMode] = useState<SearchMode>(DEFAULT_SEARCH_MODE);

  const {
    results: userResults,
    isLoading: usersLoading,
    isFetching: usersFetching,
    isError: usersError,
  } = useUserTypeaheadQuery({
    query,
    enabled: searchMode === 'users' && open,
  });
  const {
    results: classResults,
    isLoading: classesLoading,
    isFetching: classesFetching,
    isError: classesError,
  } = useClassTypeaheadQuery({
    query,
    enabled: searchMode === 'classes' && open,
  });
  const showPanel = open && query.trim().length > 0;

  const closePanel = useCallback(() => {
    setOpen(false);
  }, []);

  const clearAndClose = useCallback(() => {
    setQuery('');
    setSearchMode(DEFAULT_SEARCH_MODE);
    closePanel();
  }, [closePanel]);

  useEffect(() => {
    if (!showPanel) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        closePanel();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [closePanel, showPanel]);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (!value.trim()) {
      setSearchMode(DEFAULT_SEARCH_MODE);
    }
    setOpen(true);
  };

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
      <input
        ref={inputRef}
        type="search"
        value={query}
        onChange={(event) => handleQueryChange(event.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            clearAndClose();
            inputRef.current?.blur();
          }
        }}
        placeholder={resolvedPlaceholder}
        aria-label={resolvedPlaceholder}
        aria-expanded={showPanel}
        aria-controls={showPanel ? listboxId : undefined}
        aria-autocomplete="list"
        role="combobox"
        className={clsx(
          'w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm placeholder:text-gray-400',
          formControlSurfaceClassName,
          focusRingClassNameStrong,
        )}
      />

      {showPanel ? (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[min(24rem,70vh)] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <SearchModeToggle activeMode={searchMode} onChange={setSearchMode} />

          <div
            id={listboxId}
            role="listbox"
            className="max-h-[min(20rem,60vh)] overflow-y-auto py-1"
          >
            {searchMode === 'users' ? (
              usersError ? (
                <p className={cn(textStyles.bodyMuted, 'px-4 py-6 text-center text-red-600')}>
                  {t('usersLoadError')}
                </p>
              ) : userResults.length > 0 ? (
                <>
                  <div className="divide-y divide-gray-200">
                    {userResults.map((user) => (
                      <SearchUserResultRow key={user.id} user={user} onNavigate={clearAndClose} />
                    ))}
                  </div>
                  {usersFetching ? (
                    <p className={cn(textStyles.caption, 'px-4 py-2 text-center text-gray-400')}>
                      {t('updating')}
                    </p>
                  ) : null}
                </>
              ) : usersLoading || usersFetching ? (
                <p className={cn(textStyles.bodyMuted, 'px-4 py-6 text-center')}>
                  {t('searching')}
                </p>
              ) : (
                <p className={cn(textStyles.bodyMuted, 'px-4 py-6 text-center')}>
                  {t('noUsersFound')}
                </p>
              )
            ) : classesError ? (
              <p className={cn(textStyles.bodyMuted, 'px-4 py-6 text-center text-red-600')}>
                {t('classesLoadError')}
              </p>
            ) : classResults.length > 0 ? (
              <>
                <div className="divide-y divide-gray-200">
                  {classResults.map((classItem) => (
                    <SearchClassResultRow
                      key={`${classItem.code}-${classItem.classId}`}
                      classItem={classItem}
                      onNavigate={clearAndClose}
                    />
                  ))}
                </div>
                {classesFetching ? (
                  <p className={cn(textStyles.caption, 'px-4 py-2 text-center text-gray-400')}>
                    Updating…
                  </p>
                ) : null}
              </>
            ) : classesLoading || classesFetching ? (
              <p className={cn(textStyles.bodyMuted, 'px-4 py-6 text-center')}>{t('searching')}</p>
            ) : (
              <p className={cn(textStyles.bodyMuted, 'px-4 py-6 text-center')}>
                {t('noClassesFound')}
              </p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
